// Context interface

export interface IInfo {
    heading: string,
    showAnimation: boolean,
    streaks?: number,
    accuracy?: number,
    totalAttemptedQuestions?: number,
    completedStatusPercentage?: number,
    isAssessment?: boolean,
    assessment?: {
        answered: number,
        unanswered: number,
        coinsEarned?: number,
        /** The score out of a hundred, shown beside the coins. Left out when the screen has no score
         *  to give - a practice replay, or a teacher who chose not to show one. */
        percentage?: number
    },
    isShowStartPopup: boolean,
    isSafeZone?: boolean,
    /** A HOTS sitting: its start popup explains the coin rule rather than the 10-question one. */
    isHots?: boolean,
    isWarn?: boolean,
    isCloseFromClick: boolean,
    stopBtnText?: string,
    /** Label for the continue button. Defaults to "Continue Playing", which only fits a child
     *  mid-practice - a teacher looking through content is not playing anything. */
    continueBtnText?: string,
    /** Suppress the completed/accuracy band on a popup that ASKS rather than reports. */
    hideStats?: boolean
}

export interface IPopUp {
    info: IInfo,
    /** The dialog's main button: stop, submit or acknowledge, whatever the screen has made it say. */
    onStop: any
    onStart: any
    onContinue: any
}

export interface IAdaptivePracticeContext {
    notificationMessage: string,
    streaks?: number,
    coins?: number,
    isSafeZone: boolean,
    isLastQuestion?: boolean,
    meter: { percentage: number, level: string },
    isLoader: boolean,
    isCoinAnimation: boolean,
    isShowStartPopup: boolean,
    isShowAdaptivePopup: boolean,
    isCloseFromClick: boolean,
    engineObj: { component: { heading: any, explanation: any, hint: any }, sound: string } | any,
    reviewMode?: IReviewMode,
    options?: {
        isMusicAllowed?: boolean,
        isToggleFullSceren?: boolean,
        isShowReload?: boolean,
        isShowClose?: boolean,
        /** Show the question, do not let it be answered: nothing inside it responds to a click, a
         *  key or a drag. The quiz builder previews a question this way; a sitting never does.
         *  It also makes the frame safe to mount more than once on a page: no window-level key
         *  handling, and no scrolling itself to an anchor another frame also carries. */
        readOnly?: boolean,
        /**
         * Whether the worked explanation is ever shown - THREE states, and the difference between
         * `false` and unset matters:
         *
         *   true       show it at once, unanswered. The teacher who built the quiz asking to see
         *              the answers.
         *   false      show nothing, ever: no hint, no explanation, however they answer. A test.
         *   undefined  decide from the answer, which is what practice does: a hint on the first
         *              mistake and the explanation on the second where there are two attempts, or
         *              the explanation straight away where there is only one.
         *
         * This is deliberately separate from `singleAttempt`. How many attempts a question gets and
         * whether its answer is ever revealed are different questions, and a mode that wants one
         * attempt AND the explanation can have both.
         */
        showAnswer?: boolean,
        /** Draw the menus down each side. Off leaves the question its column and the host sizes it -
         *  the quiz builder's cards give it the full width. Default: on. */
        isShowSideMenus?: boolean,
        /**
         * What the question on screen is worth, drawn in the coin counter's place - because that is
         * what it becomes when the answer is right. Left out, nothing is drawn there.
         *
         * Here rather than inside questionSummaryHeader, where it began. Points are worth showing
         * while an attempt is read back as much as while it is answered - that is the one place a
         * student most wants to know what a question was worth - and the two are not always drawn
         * together. One home for it, read the same way whichever screen is asking.
         */
        questionPoints?: number,
        isShowExpNext?: boolean,
        /** One attempt per question, which is what a test is: the answer is taken as given and the
         *  question closes behind it - no hint, no second try, and no worked explanation. Practice
         *  leaves this off, because there a second attempt is the whole point. Opt-in, so adaptive
         *  practice and Rise are untouched. */
        singleAttempt?: boolean,
        /** Label the last question's explanation button "Finish" and end the session on it,
         *  instead of "Next". Opt-in - modes that do not set it keep today's behaviour. */
        showFinishOnLast?: boolean,
        /** Show the instructions again, mid-question. Given, the frame draws a button for it beside
         *  the other controls; left out, there is none - a screen with nothing to say does not get
         *  an empty button. */
        onShowInstructions?: () => void,
        isShowReference?: boolean,
        showReferenceScreen?: (status: boolean) => void,
        /**
         * HAND THE SITTING IN, and with it, say that there IS a sitting.
         *
         * Two things follow from it:
         *  - the row of Prev / Next under the frame is drawn, which is how a student walks a
         *    sitting forward one question at a time;
         *  - Submit joins that row once every question has been seen, and calls this.
         *
         * Left out, neither is drawn. That is what an attempt being READ BACK looks like: it is
         * walked from the strip of numbers above the question, and there is nothing to hand in -
         * the screens that open a review have already submitted, or never will.
         *
         * The row keys on THIS rather than on the strip of numbers, which both a sitting and a
         * review have, or on a flag of its own, which could be set with no Submit behind it. A
         * screen that has something to submit is being answered; one that has not is being read.
         */
        /**
         * THE ANSWER IS KEPT UNTIL THE STUDENT SAVES IT, rather than taken the moment they touch the
         * question: a tap on an option only marks it, the engine's own Done is hidden, and a Save
         * button under the frame hands in whatever the question is holding - so an answer can be
         * changed as often as they like before it counts.
         *
         * Asked for, screen by screen, and ONLY the adaptive quiz asks. It is not implied by having a
         * Submit: the Rise assessment has one and keeps answering-is-the-answer, which is how it has
         * always worked and what its hint after a wrong first attempt depends on.
         */
        saveAnswersByHand?: boolean,
        onSubmit?: () => void,
        /** What handing in is called on this screen, for the button and for the words a student
         *  reads on hover. A graded sitting submits; a practice replay finishes, since nothing is
         *  sent and nothing is marked. Left out: Submit, and "Submit your responses". */
        submitLabel?: string,
        submitTooltip?: string,
        session?: {
            isLive: boolean,
            status: string,
            onShare: () => void,
            onComplete: () => void
        }
        /**
         * THE STRIP OF QUESTION NUMBERS above the question, and everything the frame can be told
         * about them. One option, because it is one strip: a sitting being played and an attempt
         * being read back are the same row of numbers, differing only in what a number says.
         *
         * Only where to go is required. What the screen knows beyond that it gives: the clock and
         * how each question stands, each drawn only if it is here. Nothing but numbers is the
         * shortest the strip gets, and the question keeps the height the rest would have taken.
         */
        questionSummaryHeader?: {
            timer?: {
                initialTime: number,
                isStarted?: boolean,
                onTimerEnd: () => void
            },
            /** How each question stands, in the order they are drawn: it is what rings the numbers,
             *  and in a sitting what the tallies count. ONE list for both the answering and the
             *  reading back - see AssessmentQuestionStatus. Left out, a number says nothing but
             *  which one is on screen, which is all a screen walking its own questions can say. */
            questionsStatus?: AssessmentQuestionStatus[],
            current: number,
            total: number,
            setCurrentPos: (Pos: number) => void,
            /** The tallies, when the screen knows more than the strip does - a quiz carried over from
             *  an earlier sitting has questions that are not on this screen at all. Left out, the
             *  counts are taken from the strip as before. */
            answeredCount?: number,
            unansweredCount?: number
        },
        snapShot?: {
            innerHTML?: string,
            saveSnapShot: (innerHTML: string) => void,
            /** Given only when there IS something to clear - the frame shows its Clear control if
             *  and only if this is here, so the handler and the permission are the same thing.
             *  Not tied to `innerHTML`: a quiz keeps the LIVE render of the question being answered
             *  (a snapshot there looked like the question reloading), and that answer must still be
             *  clearable without leaving the question and coming back. */
            clearSnapShot?: () => void
        }
    },
    loadContent: () => void,
    saveQuestionResponse: (answerObj: { givenAnswer: any, actualAnswer: any, isCorrectAns: Boolean }, clickCount: number) => void,
    getPopUpInfo: () => IInfo,
    setShowAdaptivePopup: (isCloseFromClick?: boolean) => void,
    setShowStartPopup?: (status: boolean) => void,
    onclose: () => void
}

export interface IReviewMode {
    isAttempted: boolean,
    isCorrect: boolean,
    userInput: any,
    correctAnswer: any
}


/**
 * How one question stands, for the strip of numbers above it.
 *
 * The first three are a sitting in progress: not reached yet, reached and left, answered. None of
 * them says whether an answer is RIGHT - while a quiz is being answered nobody is told that, and
 * the frame is not given it.
 *
 * The last two are those same questions once they have been marked, which is what a review reads.
 * A question nobody answered is `unanswered` there too: nothing was given, which is not the same as
 * giving the wrong thing. `notVisited` cannot arise in a review - every question of a finished
 * attempt has been seen - and it is the one value that stops a number being opened at all.
 */
export enum AssessmentQuestionStatus {
    notVisited = "notVisited",
    unanswered = "unanswered",
    answered = "answered",
    correct = "correct",
    wrong = "wrong"
}